import React, { Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UsersIcon, LayoutGridIcon, CheckSquareIcon, ActivityIcon, BellIcon, DollarSignIcon, ArchiveIcon, BoxIcon, ShoppingCartIcon, ArrowRightIcon, LockIcon, FileTextIcon, TrendingUpIcon, ShieldCheckIcon, ClockIcon, DatabaseIcon, HeadphonesIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
const metrics = [{
  label: 'Total Users',
  value: '128',
  icon: UsersIcon,
  color: 'text-blue-600',
  bg: 'bg-blue-50'
}, {
  label: 'Active Modules',
  value: '5',
  icon: LayoutGridIcon,
  color: 'text-indigo-600',
  bg: 'bg-indigo-50'
}, {
  label: 'Pending Tasks',
  value: '23',
  icon: CheckSquareIcon,
  color: 'text-amber-600',
  bg: 'bg-amber-50'
}, {
  label: 'Uptime',
  value: '99.9%',
  icon: ActivityIcon,
  color: 'text-emerald-600',
  bg: 'bg-emerald-50'
}, {
  label: 'Alerts',
  value: '7',
  icon: BellIcon,
  color: 'text-rose-600',
  bg: 'bg-rose-50'
}, {
  label: 'Open POs',
  value: '34',
  icon: FileTextIcon,
  color: 'text-cyan-600',
  bg: 'bg-cyan-50'
}, {
  label: 'Revenue',
  value: '$1.2M',
  icon: TrendingUpIcon,
  color: 'text-purple-600',
  bg: 'bg-purple-50'
}, {
  label: 'Compliance',
  value: '98%',
  icon: ShieldCheckIcon,
  color: 'text-teal-600',
  bg: 'bg-teal-50'
}, {
  label: 'Avg Response',
  value: '1.2h',
  icon: ClockIcon,
  color: 'text-orange-600',
  bg: 'bg-orange-50'
}, {
  label: 'Records',
  value: '45K',
  icon: DatabaseIcon,
  color: 'text-gray-600',
  bg: 'bg-gray-100'
}];
const monthlyData = [{
  name: 'Jul',
  value: 42
}, {
  name: 'Aug',
  value: 58
}, {
  name: 'Sep',
  value: 35
}, {
  name: 'Oct',
  value: 72
}, {
  name: 'Nov',
  value: 65
}, {
  name: 'Dec',
  value: 88
}];
const moduleUsage = [{
  name: 'PLMS',
  value: 45,
  color: '#6366f1'
}, {
  name: 'FMS',
  value: 20,
  color: '#10b981'
}, {
  name: 'HRMS',
  value: 18,
  color: '#8b5cf6'
}, {
  name: 'AMS',
  value: 10,
  color: '#f59e0b'
}, {
  name: 'ARMS',
  value: 7,
  color: '#3b82f6'
}];
const performanceData = [{
  name: 'Mon',
  cpu: 45,
  mem: 62
}, {
  name: 'Tue',
  cpu: 52,
  mem: 58
}, {
  name: 'Wed',
  cpu: 48,
  mem: 65
}, {
  name: 'Thu',
  cpu: 61,
  mem: 70
}, {
  name: 'Fri',
  cpu: 55,
  mem: 68
}, {
  name: 'Sat',
  cpu: 32,
  mem: 45
}, {
  name: 'Sun',
  cpu: 28,
  mem: 40
}];
const modules = [{
  id: 'FMS',
  name: 'Financial Management',
  description: 'Budget, Accounts & Reports',
  icon: DollarSignIcon,
  color: 'bg-emerald-500',
  textColor: 'text-emerald-700',
  path: '/fms',
  active: false
}, {
  id: 'ARMS',
  name: 'Archive Management',
  description: 'Documents & Records',
  icon: ArchiveIcon,
  color: 'bg-blue-500',
  textColor: 'text-blue-700',
  path: '/arms',
  active: false
}, {
  id: 'HRMS',
  name: 'Human Resource',
  description: 'Employee & HR Ops',
  icon: UsersIcon,
  color: 'bg-violet-500',
  textColor: 'text-violet-700',
  path: '/hrms',
  active: false
}, {
  id: 'AMS',
  name: 'Asset Management',
  description: 'Tracking & Maintenance',
  icon: BoxIcon,
  color: 'bg-amber-500',
  textColor: 'text-amber-700',
  path: '/ams',
  active: false
}, {
  id: 'PLMS',
  name: 'Procurement & Logistics',
  description: 'Orders & Inventory',
  icon: ShoppingCartIcon,
  color: 'bg-indigo-500',
  textColor: 'text-indigo-700',
  path: '/dashboard',
  active: true
}, {
  id: 'SP',
  name: 'Service Portal',
  description: 'Tickets & Support',
  icon: HeadphonesIcon,
  color: 'bg-rose-500',
  textColor: 'text-rose-700',
  path: '/service-portal',
  active: false
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28
    }
  }
};
export function HomePage() {
  const navigate = useNavigate();
  return <div className="flex flex-col h-screen overflow-hidden bg-gray-50" data-id="element-1132">
      {/* Top Bar */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0" data-id="element-1133">
        <h1 className="text-lg font-bold text-gray-900" data-id="element-1134">
          Enterprise Resource Portal
        </h1>
        <div className="flex items-center space-x-4" data-id="element-1135">
          <div className="flex items-center space-x-3" data-id="element-1136">
            <span className="text-sm text-gray-500" data-id="element-1137">Welcome, Admin</span>
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600" data-id="element-1138">
              A
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden px-6 py-5 flex flex-col" data-id="element-1139">
        {/* Module Cards — 6 in a row (moved above dashboard) */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4 shrink-0" data-id="element-1140">
          {modules.map(mod => <motion.div key={mod.id} variants={itemVariants} whileHover={mod.active ? {
          scale: 1.03,
          y: -3
        } : {}} onClick={() => mod.active && navigate(mod.path)} className={`relative flex items-center bg-white rounded-xl border px-4 py-3.5 transition-all duration-200 overflow-hidden ${mod.active ? 'border-gray-200 shadow-sm hover:shadow-lg hover:border-indigo-300 cursor-pointer group' : 'border-gray-100 shadow-sm opacity-75 cursor-not-allowed'}`} data-id="element-1141">
              <div className={`w-10 h-10 rounded-lg ${mod.color} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300`} data-id="element-1142">
                <mod.icon className="h-5 w-5 text-white" data-id="element-1143" />
              </div>
              <div className="ml-3 flex-1 min-w-0" data-id="element-1144">
                <div className="flex items-center justify-between" data-id="element-1145">
                  <h2 className={`text-sm font-black tracking-tight ${mod.active ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1146">
                    {mod.id}
                  </h2>
                  {mod.active ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-green-100 text-green-700" data-id="element-1147">
                      Live
                    </span> : <LockIcon className="w-3 h-3 text-gray-300" data-id="element-1148" />}
                </div>
                <p className={`text-[11px] font-semibold truncate ${mod.active ? mod.textColor : 'text-gray-400'}`} data-id="element-1149">
                  {mod.name}
                </p>
              </div>
              {mod.active && <ArrowRightIcon className="ml-2 h-4 w-4 text-indigo-400 shrink-0 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" data-id="element-1150" />}
            </motion.div>)}
        </motion.div>

        {/* Metrics Row */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-5 gap-2.5 mb-4 shrink-0" data-id="element-1151">
          {metrics.map((metric, idx) => <motion.div key={idx} variants={itemVariants} className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2.5 flex items-center space-x-2.5" data-id="element-1152">
              <div className={`p-1.5 rounded-md ${metric.bg} shrink-0`} data-id="element-1153">
                <metric.icon className={`h-3.5 w-3.5 ${metric.color}`} data-id="element-1154" />
              </div>
              <div className="min-w-0" data-id="element-1155">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider truncate leading-tight" data-id="element-1156">
                  {metric.label}
                </p>
                <p className="text-base font-bold text-gray-900 leading-tight" data-id="element-1157">
                  {metric.value}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Charts Row */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-3 gap-3 mb-4 shrink-0" data-id="element-1158">
          {/* Monthly Activity Bar Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3" data-id="element-1159">
            <div className="flex items-center justify-between mb-2" data-id="element-1160">
              <h3 className="text-xs font-semibold text-gray-700" data-id="element-1161">
                Monthly Activity
              </h3>
              <span className="text-[10px] text-gray-400" data-id="element-1162">Last 6 months</span>
            </div>
            <div className="h-[100px]" data-id="element-1163">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1164">
                <BarChart data={monthlyData} barSize={16} data-id="element-1165">
                  <XAxis dataKey="name" tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} axisLine={false} tickLine={false} data-id="element-1166" />
                  <Tooltip contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb'
                }} cursor={{
                  fill: '#f3f4f6'
                }} data-id="element-1167" />
                  <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} data-id="element-1168" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Module Usage Donut */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3" data-id="element-1169">
            <div className="flex items-center justify-between mb-2" data-id="element-1170">
              <h3 className="text-xs font-semibold text-gray-700" data-id="element-1171">
                Module Usage
              </h3>
              <span className="text-[10px] text-gray-400" data-id="element-1172">This month</span>
            </div>
            <div className="flex items-center" data-id="element-1173">
              <div className="h-[100px] w-[100px] shrink-0" data-id="element-1174">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1175">
                  <PieChart data-id="element-1176">
                    <Pie data={moduleUsage} cx="50%" cy="50%" innerRadius={28} outerRadius={45} dataKey="value" strokeWidth={2} stroke="#fff" data-id="element-1177">
                      {moduleUsage.map((entry, index) => <Cell key={index} fill={entry.color} data-id="element-1178" />)}
                    </Pie>
                    <Tooltip contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: '1px solid #e5e7eb'
                  }} data-id="element-1179" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="ml-3 flex-1 space-y-1" data-id="element-1180">
                {moduleUsage.map(item => <div key={item.name} className="flex items-center justify-between" data-id="element-1181">
                    <div className="flex items-center space-x-1.5" data-id="element-1182">
                      <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: item.color
                  }} data-id="element-1183" />
                      <span className="text-[10px] font-medium text-gray-600" data-id="element-1184">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-800" data-id="element-1185">
                      {item.value}%
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>

          {/* System Performance Area Chart */}
          <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3" data-id="element-1186">
            <div className="flex items-center justify-between mb-2" data-id="element-1187">
              <h3 className="text-xs font-semibold text-gray-700" data-id="element-1188">
                System Performance
              </h3>
              <div className="flex items-center space-x-3" data-id="element-1189">
                <span className="flex items-center text-[10px] text-gray-400" data-id="element-1190">
                  <span className="w-2 h-2 rounded-full bg-indigo-400 mr-1" data-id="element-1191" />
                  CPU
                </span>
                <span className="flex items-center text-[10px] text-gray-400" data-id="element-1192">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 mr-1" data-id="element-1193" />
                  Memory
                </span>
              </div>
            </div>
            <div className="h-[100px]" data-id="element-1194">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1195">
                <AreaChart data={performanceData} data-id="element-1196">
                  <defs data-id="element-1197">
                    <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-1198">
                      <stop offset="0%" stopColor="#818cf8" stopOpacity={0.3} data-id="element-1199" />
                      <stop offset="100%" stopColor="#818cf8" stopOpacity={0} data-id="element-1200" />
                    </linearGradient>
                    <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-1201">
                      <stop offset="0%" stopColor="#34d399" stopOpacity={0.3} data-id="element-1202" />
                      <stop offset="100%" stopColor="#34d399" stopOpacity={0} data-id="element-1203" />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} axisLine={false} tickLine={false} data-id="element-1204" />
                  <Tooltip contentStyle={{
                  fontSize: 11,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb'
                }} data-id="element-1205" />
                  <Area type="monotone" dataKey="cpu" stroke="#818cf8" fill="url(#cpuGrad)" strokeWidth={2} data-id="element-1206" />
                  <Area type="monotone" dataKey="mem" stroke="#34d399" fill="url(#memGrad)" strokeWidth={2} data-id="element-1207" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>;
}